import { RequirePayoutsAccountingAdminUser } from '@/components/auth/RequirePayoutsAccountingAdminUser';
import { currencyFormatter } from '@/helpers/currencyFormatter';
import { useGetPaymentPlanQuery } from '@/services/paymentPlans';
import {
  useGetProjectByExternalIdQuery,
  useGetProjectTransactionsSummaryByExternalIdQuery,
} from '@/services/payouts';
import { Box, Button, Card, useOpenClose } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import React, { useCallback } from 'react';

import {
  AccountFundingSummaryComponentProps,
  RightAmountBox,
  SubtitleBox,
  useGetPaymentPlanSummary,
} from './AccountFundingSummary.utils';
import { AccountPaymentPlanModal } from './modals/AccountPaymentPlanModal';

export const AccountFundingSummary = ({ account }: AccountFundingSummaryComponentProps) => {
  const { data: project, refetch: refetchProject } = useGetProjectByExternalIdQuery(
    account?.id?.toString() || skipToken,
  );
  const { data: paymentPlan, isLoading } = useGetPaymentPlanQuery(
    project?.planId ? { id: project?.planId } : skipToken,
  );

  const { data: accountTransactionsSummary, refetch: refetchTransactionsSummary } =
    useGetProjectTransactionsSummaryByExternalIdQuery(
      {
        projectExternalId: account?.id?.toString(),
      },
      { skip: !account?.id },
    );

  const {
    isOpen: isAccountPaymentPlanModalOpen,
    handleOpen: handleOpenAccountPaymentPlanModal,
    handleClose: handleCloseAccountPaymentPlanModal,
  } = useOpenClose();

  const refetchProjectAndTransactions = useCallback(() => {
    refetchProject();
    refetchTransactionsSummary();
  }, [refetchProject, refetchTransactionsSummary]);

  const projectTotalValue = accountTransactionsSummary?.projectTotalCost ?? 0;
  const payoutAmountToDate = accountTransactionsSummary?.payoutAmountToDate ?? 0;
  const amountRemaining = accountTransactionsSummary?.remainingPayout ?? 0;

  const dcHoldback = project?.attributes?.domesticContentHoldbackAmount ?? 0;
  const standardHoldback = project?.attributes?.standardHoldbackAmount ?? 0;
  const totalHoldback = project?.attributes?.totalHoldbackAmount ?? 0;

  const paymentPlanElements = useGetPaymentPlanSummary(accountTransactionsSummary) || [];

  return (
    <Box gap="md" background="primary" radius="md">
      <AccountPaymentPlanModal
        isModalOpen={isAccountPaymentPlanModalOpen}
        handleClose={handleCloseAccountPaymentPlanModal}
        project={project}
        paymentPlan={paymentPlan}
        refetchProjectAndTransactions={refetchProjectAndTransactions}
        account={account}
      />
      <Card padding={'lg'}>
        <Card.Header>
          <Box
            direction="row"
            justifyContent="space-between"
            style={{
              marginLeft: '-1em',
            }}
          >
            <SubtitleBox>PAYMENT PLAN</SubtitleBox>
            <RequirePayoutsAccountingAdminUser>
              <Button
                iconPrefix="settings"
                size="sm"
                variant="secondary"
                tone="neutral"
                disabled={isLoading}
                onClick={handleOpenAccountPaymentPlanModal}
              >
                Change Plan
              </Button>
            </RequirePayoutsAccountingAdminUser>
          </Box>
        </Card.Header>
        <Box
          direction={'row'}
          style={{
            marginTop: '10px',
          }}
        >
          <Box
            style={{
              flex: '1',
            }}
          >
            <Box direction="row">
              {paymentPlanElements?.map((element) => (
                <React.Fragment key={element.id}>{element.component}</React.Fragment>
              ))}
            </Box>
          </Box>
          <Box
            direction="row"
            style={{
              flex: '0.7',
            }}
            gap="md"
            justifyContent="center"
          >
            <Box
              style={{
                flex: '1',
              }}
              background="grey-50"
              radius="md"
              justifyContent="space-between"
              padding="sm"
            >
              <RightAmountBox
                containerStyle={{ borderColor: 'grey-200', borderWidth: '0 0 xs 0', padding: '0 0 2xs' }}
                title="Total Project Value"
                amount={currencyFormatter.format(projectTotalValue)}
              />
              <RightAmountBox
                title="Payout to-date"
                amount={currencyFormatter.format(payoutAmountToDate ?? 0)}
                tooltipText="The amount that has been paid to the receiving bank."
              />
              <RightAmountBox
                title="Amount Remaining"
                amount={currencyFormatter.format(amountRemaining)}
                tooltipText="The remainder of the payout amount, excluding holdbacks."
              />
            </Box>
            <Box
              style={{
                flex: '1',
              }}
              background="grey-50"
              radius="md"
              justifyContent="space-between"
              padding="sm"
            >
              <RightAmountBox
                containerStyle={{ borderColor: 'grey-200', borderWidth: '0 0 xs 0', padding: '0 0 2xs' }}
                title="Total Holdback"
                amount={currencyFormatter.format(totalHoldback)}
              />
              <RightAmountBox
                title="DC Holdback (if eligible)"
                amount={currencyFormatter.format(dcHoldback)}
                tooltipText="Domestic content holdback amount."
              />
              <RightAmountBox
                title="Standard Holdback"
                amount={currencyFormatter.format(standardHoldback)}
                tooltipText="Standard holdback amount."
              />
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
